import {
    featureFlags,
    FeatureFlagClientNotInitializedError,
    isTestAccount,
    IUserData,
    OrganizationHandle,
    SubscriptionManager,
} from "@buildwithflux/core";
import {
    areWeInStorybook,
    areWeTestingWithJest,
    getEnvironmentName,
    Logger,
    Records,
    Unsubscriber,
} from "@buildwithflux/shared";
import {initialize, LDClient, LDContext, LDFlagSet} from "launchdarkly-js-client-sdk";
import {LDMultiKindContext, LDSingleKindContext} from "launchdarkly-js-sdk-common";
import {isEqual, mapValues} from "lodash";

import {CurrentUserService} from "modules/auth";
import {OrganizationStoreState, UseOrganizationStore} from "modules/auth/state/organization";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * Represents whether the LaunchDarkly context matches the current auth state.
 * - "unsettled": Auth hasn't resolved yet, we don't know who the user is
 * - "settled": LD context matches the current auth state
 * - "error": LaunchDarkly failed to initialize or is unreachable (e.g., blocked by adblocker)
 */
export type ContextSyncState = "unsettled" | "settled" | "error";

/**
 * Represents the freshness of the current flag values.
 * - "invalid": Context is wrong, flags cannot be trusted
 * - "cached": Flags are from localStorage bootstrap for the correct context
 * - "fresh": Flags are confirmed from LaunchDarkly server for the correct context
 * - "defaults": Using SDK default values (least permissions) due to error/unavailability
 */
export type FreshnessState = "invalid" | "cached" | "fresh" | "defaults";

/**
 * Two-dimensional lifecycle state for feature flags.
 * - contextSync: Is the LD context correct for the current auth state?
 * - freshness: Where did the current flag values come from?
 * - cachedToFreshDurationMs: Time in ms from "cached" to "fresh" state (only set on fresh transition)
 *
 * Invariant: When contextSync === "unsettled", freshness is always "invalid"
 */
export interface FeatureFlagLifecycleState {
    contextSync: ContextSyncState;
    freshness: FreshnessState;
    /** Duration in ms from "cached" to "fresh" state. Only set when freshness transitions to "fresh". */
    cachedToFreshDurationMs?: number;
}

// IMPORTANT:
// LD bills based on number of monthly active contexts. Active contexts are defined by the number of unique keys that are
// tracked at *initialize* time or when identifying a context via *identify*. If a key is not specified in either
// scenario, LD creates a unique key for that user/session. Since we initialize the LD client before we have a user
// context, LD recommends to always initialize with the same user key, then replace it via *identify* once we know
// who the user is. Otherwise every session generates a new unique key (which we pay for).
// See LD documentation @ https://docs.launchdarkly.com/sdk/features/user-config#anonymous-users-in-the-nodejs-sdk
// See internal documentation @ [workplace-post/670807980925056](/docs/workplace/670807980925056.md)
const anonymousUser: LDSingleKindContext = {
    kind: "user",
    key: "anonymous-user",
    anonymous: true,
};
const initialUser: LDSingleKindContext = {
    kind: "user",
    key: "initial-user",
    anonymous: true,
};
const testUser: LDSingleKindContext = {
    kind: "user",
    key: "test-user",
    anonymous: false,
};

function isMultiKindContext(context: LDContext | undefined): context is LDMultiKindContext {
    return !!context && (context as LDMultiKindContext)?.kind === "multi";
}

export function isMultiKindContextWithUser(
    context: LDContext | undefined,
): context is LDMultiKindContext & {user: LDSingleKindContext} {
    return isMultiKindContext(context) && !!(context as LDMultiKindContext)?.user;
}

export type FlagSet = Record<string, unknown> | undefined | null; // LDFlagSet is essentially this, but with any

export interface AbstractLaunchDarklyService {
    allFlags(): FlagSet | undefined;
    subscribeToFlags(onChange: (flagValues: FlagSet) => void): Unsubscriber;
    shutdown(): Promise<void>;

    /**
     * Get the current lifecycle state of the feature flag service.
     * This indicates whether flags can be trusted for the current user context.
     */
    getLifecycleState(): FeatureFlagLifecycleState;

    /**
     * Subscribe to lifecycle state changes.
     * Callback is invoked immediately with current state and on every state change.
     */
    subscribeToLifecycleState(onChange: (state: FeatureFlagLifecycleState) => void): Unsubscriber;

    /**
     * Subscribe to flags only when they are ready (freshness !== "invalid").
     * Callback is NOT invoked if flags are invalid, only when context is settled.
     */
    subscribeToFlagsWhenReady(onChange: (flagValues: FlagSet) => void): Unsubscriber;

    /**
     * Subscribe to flags only when they are fresh from the server.
     * Callback is NOT invoked until flags have been confirmed from LaunchDarkly server.
     */
    subscribeToFlagsWhenFresh(onChange: (flagValues: FlagSet) => void): Unsubscriber;
}

/**
 * IMPORTANT NOTE: Please don't call this class directly in React components
 *
 * Please see PR description in https://github.com/buildwithflux/flux-app/pull/8833,
 * about how LaunchDarkly works in Flux, how flag values get changed, and the responsibility
 * of this class
 *
 * See https://github.com/buildwithflux/flux-app/pull/8722 for why we use an initialUser
 */
export class LaunchDarklyService implements AbstractLaunchDarklyService {
    protected client?: LDClient;

    private isInitialized = false;
    private isIntializing = false;
    private isShutDown = false;

    private subscriptionManager: SubscriptionManager<
        "featureFlags" | "lifecycleState",
        FlagSet | FeatureFlagLifecycleState
    >;
    private currentFlagSet: FlagSet;
    private currentContext: LDMultiKindContext | undefined;
    private enterpriseHandles: OrganizationHandle[] = [];
    private readonly siteContext: LDSingleKindContext;

    private organizationStoreUnsubscriber: Unsubscriber | undefined = undefined;
    private currentUserServiceUnsubscriber: Unsubscriber | undefined = undefined;

    // Lifecycle state tracking
    private lifecycleState: FeatureFlagLifecycleState = {
        contextSync: "unsettled",
        freshness: "invalid",
    };
    // Track the expected context signature to detect when identify() completes for the right context.
    // This includes user key AND other targeting attributes (like enterprise handles) so we can
    // distinguish between identity changes (user key changed -> invalid) and attribute changes
    // (same user, different attributes -> cached, not invalid).
    private expectedContextSignature: string | undefined = undefined;
    // Track just the user key separately to distinguish identity changes from attribute changes
    private expectedUserKey: string | undefined = undefined;
    // Track whether we've received fresh flags from the server (not just cached)
    private hasReceivedFreshFlags = false;
    // Track timestamp when we entered "cached" state for analytics
    private cachedStateTimestamp: number | undefined = undefined;
    // Timeout for detecting when LaunchDarkly is blocked/unreachable (e.g., by adblocker)
    // If we're stuck in "invalid" freshness for longer than this, transition to error state
    private static readonly INIT_TIMEOUT_MS = 5000;
    private initTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

    constructor(
        private readonly currentUserServiceProvider: () => CurrentUserService,
        private readonly useOrganizationStoreProvider: () => UseOrganizationStore,
        private readonly logger: Logger,
    ) {
        this.subscriptionManager = new SubscriptionManager();

        this.siteContext = {
            kind: "site",
            key: getEnvironmentName() ?? "unknown",
        };

        if (!areWeTestingWithJest() && !areWeInStorybook()) {
            // Wait for auth to be ready before initializing LaunchDarkly.
            // This prevents caching flags for the 'initial-user' context, which caused
            // stale flag values to persist in localStorage and affect users weeks later.
            // See: https://app.devin.ai/sessions/4e85dbd567124c25bd62e518d2821840
            this.waitForAuthThenInit();
        }
    }

    /**
     * Waits for the first auth state change that indicates a stable user identity
     * (not partiallyAuthenticated) before initializing the LaunchDarkly client.
     *
     * This ensures we never initialize with the 'initial-user' context, which would
     * cache potentially stale flag values in localStorage.
     */
    private waitForAuthThenInit(): void {
        const unsubscribe = this.currentUserServiceProvider().subscribeToUserChanges((userData) => {
            // Wait until we have a stable auth state (not partially authenticated)
            // partiallyAuthenticated means Firebase user exists but Flux user doc isn't loaded yet
            if (userData.suggestedState === "partiallyAuthenticated") {
                return;
            }

            // We have a stable auth state - either a real user, anonymous user, or logged out
            // Pass the user directly to initClient to avoid race conditions where
            // getCurrentUser() might return a different value
            unsubscribe();
            void this.initClientWithUser(userData.user);
        });
    }

    public async shutdown(): Promise<void> {
        this.isShutDown = true;
        // Clear the init timeout if it's still pending
        if (this.initTimeoutId) {
            clearTimeout(this.initTimeoutId);
            this.initTimeoutId = undefined;
        }
        this.currentUserServiceUnsubscriber?.();
        this.organizationStoreUnsubscriber?.();
        await this.client?.close();
    }

    public async identifyUser(user: IUserData | null | undefined): Promise<LDFlagSet | undefined> {
        if (!this.client || !this.isInitialized) {
            const error = new FeatureFlagClientNotInitializedError(
                new Error(`identifyUser called on uninitialized service`),
            );
            this.logger.error(error);
            FluxLogger.captureError(error);
            return;
        }

        if (this.isShutDown) {
            const error = new FeatureFlagClientNotInitializedError(
                new Error(`identifyUser called on shut down service`),
            );
            this.logger.error(error);
            FluxLogger.captureError(error);
            return;
        }

        const context = this.contextForUser(user ?? undefined);
        const userContext: LDSingleKindContext | undefined = context?.user as LDSingleKindContext | undefined;
        const newUserKey = userContext?.key;
        const newContextSignature = this.computeContextSignature(context);

        const userKeyChanged = newUserKey !== this.expectedUserKey;
        const contextSignatureChanged = newContextSignature !== this.expectedContextSignature;

        if (userKeyChanged) {
            // User identity changed (e.g., login/logout) - mark as invalid until identify completes
            // This blocks the UI because we can't trust flags for a different user
            this.updateLifecycleState("unsettled", "invalid");
            this.expectedUserKey = newUserKey;
            this.expectedContextSignature = newContextSignature;
            this.hasReceivedFreshFlags = false;
        } else if (contextSignatureChanged) {
            // Same user identity, but context attributes changed (e.g., enterprise membership)
            // Downgrade to "cached" (not "invalid") - flags are usable but may be stale
            // This does NOT block the UI since the user identity is still correct
            this.expectedContextSignature = newContextSignature;
            this.hasReceivedFreshFlags = false;
            if (this.lifecycleState.freshness === "fresh") {
                this.updateLifecycleState("settled", "cached");
            }
        }

        await this.client.identify(context);

        // After identify completes, context is settled with cached flags
        // The "change" event handler will update to "fresh" when server responds
        this.updateLifecycleState("settled", "cached");

        if (
            userContext?.key === "anonymous-user" ||
            userContext?.key === "initial-user" ||
            userContext?.key === "test-user"
        ) {
            // In case feature flags for test user is the same for initial user, call `updateStateAndNotify`
            // manually when finish identifying
            this.updateStateAndNotify(this.client?.allFlags());
        }
    }

    public allFlags() {
        return this.client?.allFlags();
    }

    public subscribeToFlags(onChange: (flagValues: FlagSet) => void): Unsubscriber {
        const currentValue = this.client?.allFlags();
        if (currentValue != null && !Records.isEmpty(currentValue)) {
            onChange(currentValue);
        }
        return this.subscriptionManager.addSubscription("featureFlags", onChange);
    }

    public getLifecycleState(): FeatureFlagLifecycleState {
        return {...this.lifecycleState};
    }

    public subscribeToLifecycleState(onChange: (state: FeatureFlagLifecycleState) => void): Unsubscriber {
        // Note: We do NOT immediately invoke the callback here because this method
        // is called during Zustand store initialization, and the store's state
        // is not yet available at that point. Callers should use getLifecycleState()
        // to get the initial state if needed.
        return this.subscriptionManager.addSubscription(
            "lifecycleState",
            onChange as (value: FlagSet | FeatureFlagLifecycleState) => void,
        );
    }

    public subscribeToFlagsWhenReady(onChange: (flagValues: FlagSet) => void): Unsubscriber {
        // Only invoke callback when freshness !== "invalid"
        const wrappedCallback = (flagValues: FlagSet) => {
            if (this.lifecycleState.freshness !== "invalid") {
                onChange(flagValues);
            }
        };

        // If currently ready, invoke immediately
        const currentValue = this.client?.allFlags();
        if (this.lifecycleState.freshness !== "invalid" && currentValue != null && !Records.isEmpty(currentValue)) {
            onChange(currentValue);
        }

        return this.subscriptionManager.addSubscription("featureFlags", wrappedCallback);
    }

    public subscribeToFlagsWhenFresh(onChange: (flagValues: FlagSet) => void): Unsubscriber {
        // Only invoke callback when freshness === "fresh"
        const wrappedCallback = (flagValues: FlagSet) => {
            if (this.lifecycleState.freshness === "fresh") {
                onChange(flagValues);
            }
        };

        // If currently fresh, invoke immediately
        const currentValue = this.client?.allFlags();
        if (this.lifecycleState.freshness === "fresh" && currentValue != null && !Records.isEmpty(currentValue)) {
            onChange(currentValue);
        }

        return this.subscriptionManager.addSubscription("featureFlags", wrappedCallback);
    }

    /**
     * Initialize the LaunchDarkly client with the provided user.
     * This is called by waitForAuthThenInit after auth state is stable.
     */
    protected async initClientWithUser(user: IUserData | undefined): Promise<void> {
        if (this.isInitialized || this.isIntializing || this.isShutDown) {
            return;
        }

        this.isIntializing = true;

        const clientKey = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_KEY_PRODUCTION;

        if (!clientKey) {
            throw new Error("REACT_APP_LAUNCHDARKLY_CLIENT_KEY_PRODUCTION is not set");
        }

        // Use the user passed from waitForAuthThenInit to ensure we have the correct
        // auth state. This prevents using 'initial-user' context which caused stale
        // flag values to persist in localStorage.
        const initialContext = this.contextForUser(user);

        // Track the expected context for lifecycle state management
        const userContext = initialContext.user as LDSingleKindContext | undefined;
        this.expectedUserKey = userContext?.key;
        this.expectedContextSignature = this.computeContextSignature(initialContext);

        const clientLogger = this.logger.child({name: "LaunchDarklyService.client"}, {level: "warn"});

        this.client = initialize(clientKey, initialContext, {
            streaming: true,
            bootstrap: "localStorage",
            logger: clientLogger,
        });

        // Start a timeout to detect if LaunchDarkly is blocked/unreachable (e.g., by adblocker)
        // If we're still in "invalid" state after the timeout, transition to error state
        this.initTimeoutId = setTimeout(() => {
            if (this.lifecycleState.freshness === "invalid") {
                // LaunchDarkly failed to initialize in time - transition to error state
                // Use "defaults" freshness since we have no usable flags
                this.updateLifecycleState("error", "defaults");
                this.isInitialized = true; // Mark as initialized so the app can proceed
                this.isIntializing = false;
                // Notify with all flags set to undefined so the app uses default (least permissions) values
                // Using mapValues to set all flags to undefined ensures mergeFlags() properly resets them
                this.subscriptionManager.notify(
                    "featureFlags",
                    mapValues(featureFlags, () => undefined),
                );
                this.subscribeToUserChanges();
                this.subscribeToOrganizationChanges();
            }
        }, LaunchDarklyService.INIT_TIMEOUT_MS);

        await this.client
            .waitForInitialization()
            .then(() => {
                // Clear the timeout since initialization succeeded
                if (this.initTimeoutId) {
                    clearTimeout(this.initTimeoutId);
                    this.initTimeoutId = undefined;
                }

                if (!this.client) {
                    throw new Error("failed to initialize LD client");
                }
                this.isInitialized = true;

                // After waitForInitialization, we have flags (possibly from localStorage cache)
                // Context is now settled, freshness is "cached" (bootstrap from localStorage)
                this.updateLifecycleState("settled", "cached");

                const currentFlagValues = this.client?.allFlags();
                this.updateStateAndNotify(currentFlagValues);

                this.client.on("change", () => {
                    // When we receive a "change" event, flags are fresh from the server
                    this.hasReceivedFreshFlags = true;
                    if (this.lifecycleState.contextSync === "settled") {
                        this.updateLifecycleState("settled", "fresh");
                    }
                    this.updateStateAndNotify(this.client?.allFlags());
                });

                this.subscribeToUserChanges();
                this.subscribeToOrganizationChanges();
            })
            .catch((error) => {
                // Clear the timeout since we got an error response
                if (this.initTimeoutId) {
                    clearTimeout(this.initTimeoutId);
                    this.initTimeoutId = undefined;
                }
                // Transition to error state with defaults
                this.updateLifecycleState("error", "defaults");
                this.isInitialized = true;
                // Notify with all flags set to undefined so the app uses default (least permissions) values
                // Using mapValues to set all flags to undefined ensures mergeFlags() properly resets them
                this.subscriptionManager.notify(
                    "featureFlags",
                    mapValues(featureFlags, () => undefined),
                );
                // we want to know when this fails to initialize but it shouldn't block the user
                FluxLogger.captureError(new FeatureFlagClientNotInitializedError(error));
                this.subscribeToUserChanges();
                this.subscribeToOrganizationChanges();
            });
    }

    /**
     * @deprecated Use initClientWithUser instead. This method is kept for backward
     * compatibility with TestLaunchDarklyService.
     */
    protected async initClient(): Promise<void> {
        const currentUser = this.currentUserServiceProvider().getCurrentUser();
        return this.initClientWithUser(currentUser);
    }

    private subscribeToUserChanges(): void {
        this.currentUserServiceUnsubscriber = this.currentUserServiceProvider().subscribeToUserChanges((user) => {
            if (user.user && user.suggestedState !== "partiallyAuthenticated") {
                void this.identifyUser(user.user);
            }
        });
    }

    private subscribeToOrganizationChanges(): void {
        this.organizationStoreUnsubscriber = this.useOrganizationStoreProvider().subscribe(
            this.processOrganizationState.bind(this),
        );
        this.processOrganizationState(this.useOrganizationStoreProvider().getState());
    }

    private processOrganizationState(state: OrganizationStoreState): void {
        const enterpriseHandles = state.memberships
            .filter((membership) => membership.organization.visibility === "unlisted")
            .map((membership) => membership.organization.handle)
            .sort();

        if (!isEqual(enterpriseHandles, this.enterpriseHandles)) {
            this.enterpriseHandles = enterpriseHandles;
            void this.identifyUser(this.currentUserServiceProvider().getCurrentUser());
        }
    }

    private updateStateAndNotify(newFlags: FlagSet) {
        const newContext = this.client?.getContext();
        const contextHasIdentifiedUser =
            !!newContext && isMultiKindContextWithUser(newContext) && newContext.user.key !== initialUser.key;
        const flagsChanged = !isEqual(this.currentFlagSet, newFlags);
        const contextChanged = !isEqual(this.currentContext, newContext);

        // See more detail in https://github.com/buildwithflux/flux-app/pull/8722 about why
        // we setup these conditions here.
        // These conditions are tested by `LaunchDarklyService.test.ts`
        if (contextHasIdentifiedUser && (flagsChanged || contextChanged)) {
            this.currentFlagSet = newFlags;
            this.currentContext = newContext;
            this.subscriptionManager.notify("featureFlags", newFlags);
        }
    }

    /**
     * Updates the lifecycle state and notifies subscribers.
     * Includes cachedToFreshDurationMs when transitioning from "cached" to "fresh".
     */
    private updateLifecycleState(contextSync: ContextSyncState, freshness: FreshnessState): void {
        const previousState = this.lifecycleState;
        const stateChanged = previousState.contextSync !== contextSync || previousState.freshness !== freshness;

        if (!stateChanged) {
            return;
        }

        // Track timing when entering "cached" state
        if (freshness === "cached" && previousState.freshness !== "cached") {
            this.cachedStateTimestamp = Date.now();
        }

        // Calculate duration when transitioning from "cached" to "fresh"
        let cachedToFreshDurationMs: number | undefined;
        if (freshness === "fresh" && previousState.freshness === "cached" && this.cachedStateTimestamp !== undefined) {
            cachedToFreshDurationMs = Date.now() - this.cachedStateTimestamp;
        }

        this.lifecycleState = {contextSync, freshness, cachedToFreshDurationMs};
        this.subscriptionManager.notify("lifecycleState", this.lifecycleState);
    }

    /**
     * Computes a signature string for the given context that includes all targeting attributes.
     * This is used to detect when context attributes change (e.g., enterprise handles) even if
     * the user identity (key) stays the same.
     */
    private computeContextSignature(context: LDMultiKindContext): string {
        const userContext = context.user as LDSingleKindContext | undefined;
        const siteContext = context.site as LDSingleKindContext | undefined;
        const parts = [
            userContext?.key ?? "",
            userContext?.email ?? "",
            (userContext?.enterprise_organization_handles as string[] | undefined)?.join(",") ?? "",
            siteContext?.key ?? "",
        ];
        return parts.join("|");
    }

    private contextForUser(currentUser: IUserData | undefined): LDMultiKindContext {
        if (!currentUser) {
            return {
                kind: "multi",
                user: initialUser,
                site: this.siteContext,
            };
        }

        if (currentUser.isAnonymous) {
            return {
                kind: "multi",
                user: anonymousUser,
                site: this.siteContext,
            };
        }

        if (isTestAccount(currentUser, "strict")) {
            return {
                kind: "multi",
                user: testUser,
                site: this.siteContext,
            };
        }

        const user: LDSingleKindContext = {
            kind: "user",
            key: currentUser.handle,
            anonymous: false,
            email: currentUser.email,
            name: currentUser.full_name,
            created_at: currentUser.created_at,
        };

        // Length check so we don't create too many duplicate contexts, because this logic is not present in the backend yet
        if (this.enterpriseHandles.length > 0) {
            user.enterprise_organization_handles = this.enterpriseHandles;
        }

        return {
            kind: "multi",
            user,
            site: this.siteContext,
        };
    }
}

export class TestLaunchDarklyService extends LaunchDarklyService {
    public override async initClient(): Promise<void> {
        await super.initClient();
    }

    public getContext() {
        return this.client?.getContext();
    }
}

export class MockLaunchDarklyService implements AbstractLaunchDarklyService {
    private lifecycleState: FeatureFlagLifecycleState = {
        contextSync: "settled",
        freshness: "fresh",
    };

    constructor(private readonly flags: FlagSet = {}, initialLifecycleState?: FeatureFlagLifecycleState) {
        if (initialLifecycleState) {
            this.lifecycleState = initialLifecycleState;
        }
    }

    allFlags(): FlagSet | undefined {
        return this.flags;
    }

    subscribeToFlags(_onChange: (flagValues: FlagSet) => void): Unsubscriber {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
    }

    getLifecycleState(): FeatureFlagLifecycleState {
        return {...this.lifecycleState};
    }

    subscribeToLifecycleState(_onChange: (state: FeatureFlagLifecycleState) => void): Unsubscriber {
        // Note: We do NOT immediately invoke the callback here to match the real
        // LaunchDarklyService behavior. Callers should use getLifecycleState()
        // to get the initial state if needed.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
    }

    subscribeToFlagsWhenReady(onChange: (flagValues: FlagSet) => void): Unsubscriber {
        if (this.lifecycleState.freshness !== "invalid") {
            onChange(this.flags);
        }
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
    }

    subscribeToFlagsWhenFresh(onChange: (flagValues: FlagSet) => void): Unsubscriber {
        if (this.lifecycleState.freshness === "fresh") {
            onChange(this.flags);
        }
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
    }

    shutdown(): Promise<void> {
        return Promise.resolve();
    }
}
